import { Request, Response } from 'express';
import BUSINESS_USER_FEATURES from '@/server/constants/businessAccountFeatures';

/**
 * Generates schema.org JSON-LD structured data for seller profile pages
 * Returns AutoDealer, LocalBusiness, or ProfilePage+Person based on seller features
 * Only emits on canonical URLs (no filters: l1/l2/query/location/attributesKeys/year/pagination)
 */
export default function getSellerProfileSchema(
  req: Request,
  res: Response,
  canonicalUrl: string,
): TSellerProfileSchema | null {
  // Guard: req and res must exist
  if (!req || !res) {
    return null;
  }

  const seller = res.locals?.seller;
  const requestData = res.locals?.requestData;

  // Guard: seller must exist
  if (!seller) {
    return null;
  }

  const sellerBusinessFeatures: string[] = seller?.businessFeatures ?? [];

  // Guard: only emit on canonical URLs (no filters)
  const { l1Category, l2Category, searchQuery, location, attributesValuesKeys, attributesValuesIds, year, pagination } =
    res.locals || {};
  const hasAttributes =
    (Array.isArray(attributesValuesKeys) && attributesValuesKeys.length > 0) ||
    (Array.isArray(attributesValuesIds) && attributesValuesIds.length > 0);
  const hasPagination = Boolean(pagination && pagination.offset > 0);
  if (l1Category || l2Category || searchQuery || location || hasAttributes || year || hasPagination) {
    return null;
  }

  // Extract seller data
  const sellerName: string = seller.title || seller.name || '';
  const sellerId = seller.id;
  if (!sellerName || !sellerId || !canonicalUrl) {
    return null;
  }

  // Use the pre-computed canonical URL for consistency with page canonical tag
  const sellerUrl = canonicalUrl;

  // Extract locale and country
  // Note: requestData.tenant.locale is used by lrp.tsx (SSR), while requestData.tenantContext.locale
  // is used by sellerModel. Check both paths to ensure compatibility across different request contexts.
  const locale: string = requestData?.tenant?.locale || requestData?.tenantContext?.locale || 'nl-NL';
  const addressCountry = locale.split('-')[1] || 'NL';

  // Calculate aggregate rating (weighted average)
  const reviews: TReviewItem[] = Array.isArray(seller.reviews) ? seller.reviews : [];
  let totalWeightedRating = 0;
  let totalReviewCount = 0;
  reviews.forEach((review) => {
    const rating = review.rating;
    const count = review.numberOfReviews;
    if (typeof rating === 'number' && typeof count === 'number' && count > 0) {
      totalWeightedRating += rating * count;
      totalReviewCount += count;
    }
  });

  const aggregateRating =
    totalReviewCount > 0
      ? {
          '@type': 'AggregateRating',
          ratingValue: Math.round((totalWeightedRating / totalReviewCount) * 10) / 10,
          reviewCount: totalReviewCount,
          bestRating: 5,
          worstRating: 1,
        }
      : undefined;

  // Build address object
  const sellerCity = seller.location;
  const streetAddress = seller.streetAndHouseNumber;
  const postalCode = seller.postcode;

  const address = {
    '@type': 'PostalAddress',
    ...(streetAddress && { streetAddress }),
    ...(sellerCity && { addressLocality: sellerCity }),
    ...(postalCode && { postalCode }),
    addressCountry,
  };

  // Determine seller type based on features
  // AutoDealer: Only use DEALER_ACCESS trait from user-feature-service (authoritative source)
  const isAutoDealer = sellerBusinessFeatures.includes(BUSINESS_USER_FEATURES.DEALER_ACCESS);
  const isBusiness = !isAutoDealer && sellerBusinessFeatures.includes(BUSINESS_USER_FEATURES.PROFILE);

  if (isAutoDealer) {
    return {
      '@context': 'https://schema.org',
      '@type': 'AutoDealer',
      name: sellerName,
      url: sellerUrl,
      address,
      ...(aggregateRating && { aggregateRating }),
    };
  }

  if (isBusiness) {
    return {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: sellerName,
      url: sellerUrl,
      address,
      ...(aggregateRating && { aggregateRating }),
    };
  }

  // Case 3: ProfilePage + Person (C2C seller, no business features)
  // Note: aggregateRating is not supported on Person type per schema.org
  return {
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    mainEntity: {
      '@type': 'Person',
      name: sellerName,
      url: sellerUrl,
    },
  };
}
